<template>
  <div class="dispatch-page" ref="pageRoot">
    <div class="stats-grid">
      <div v-for="card in statCards" :key="card.key" class="stat-card">
        <div class="stat-icon" :class="card.iconClass">
          <a-icon :type="card.icon" />
        </div>
        <div class="stat-body">
          <p class="stat-label">{{ card.label }}</p>
          <p class="stat-value">{{ stats[card.key] }}</p>
        </div>
      </div>
    </div>

    <div class="pool-card pool-card-fill">
      <div class="pool-header" ref="poolHeader">
        <div class="pool-title-row">
          <h2 class="pool-title">待指派订单池</h2>
          <a-tag v-if="stats.pending > 0" color="red">{{ stats.pending }}个待处理</a-tag>
        </div>
        <div class="pool-actions">
          <a-input
            v-model="filters.keyword"
            allowClear
            class="query-input"
            placeholder="搜索订单号/医院/患者"
            @pressEnter="handleSearch"
          />
          <a-select v-model="filters.status" class="query-select" @change="handleSearch">
            <a-select-option value="all">全部状态</a-select-option>
            <a-select-option value="pending">待指派</a-select-option>
            <a-select-option value="pending_confirm">待确认</a-select-option>
            <a-select-option value="serving">服务中</a-select-option>
            <a-select-option value="done">已完工</a-select-option>
          </a-select>
          <a-button type="primary" icon="search" @click="handleSearch">查询</a-button>
          <a-button icon="reload" @click="resetFilters">重置</a-button>
        </div>
      </div>

      <div class="table-host" ref="tableHost" :style="tableHostStyle">
        <div class="table-body-wrap" :style="tableBodyStyle">
          <a-table
            :columns="columns"
            :dataSource="orders"
            :pagination="false"
            rowKey="id"
            :loading="loading"
            class="order-table"
            :scroll="{ x: 1280, y: tableScrollY }"
          >
          <template slot="orderNo" slot-scope="text">
            <span class="order-no">{{ text }}</span>
          </template>

          <template slot="location" slot-scope="text, record">
            <div class="cell-location">
              <p class="hospital-name">{{ record.hospital }}</p>
              <p class="dept-text">{{ record.department }} · {{ record.bedNo }}</p>
            </div>
          </template>

          <template slot="patient" slot-scope="text, record">
            <div class="cell-patient">
              <span class="patient-name">{{ record.patientName }}</span>
              <span class="patient-meta">
                {{ record.patientAge }}岁 · {{ record.patientGender }} · {{ record.careLevel }}
              </span>
            </div>
          </template>

          <template slot="assign" slot-scope="text, record">
            <div class="cell-assign">
              <a-tag :color="assignTagColor(record.assignMode)">{{ record.assignModeLabel }}</a-tag>
              <p v-if="record.preferredCaregiverName && !record.caregiverName" class="assign-sub">
                意向：{{ record.preferredCaregiverName }}
              </p>
              <p v-else-if="record.caregiverName" class="assign-sub">护工：{{ record.caregiverName }}</p>
              <p v-if="record.assignOperatorName" class="assign-sub assign-operator">
                指派账号：{{ record.assignOperatorName }}
              </p>
            </div>
          </template>

          <template slot="status" slot-scope="text, record">
            <a-tag :color="statusTagColor(text)">
              {{ record.statusLabel || (statusMap[text] && statusMap[text].label) }}
            </a-tag>
          </template>

          <template slot="action" slot-scope="text, record">
            <div class="action-cell">
              <div class="action-links">
                <template v-if="record.status !== 'done'">
                  <a class="action-text action-assign" @click="openAssign(record)">
                    {{ record.status === "pending" ? "指派护工" : "改派护工" }}
                  </a>
                  <a-popconfirm
                    v-if="record.canComplete"
                    title="确认标记为已完工？"
                    ok-text="确认完工"
                    cancel-text="取消"
                    @confirm="handleComplete(record)"
                  >
                    <a
                      class="action-text action-complete"
                      :class="{ 'is-loading': completingId === record.id }"
                    >
                      {{ completingId === record.id ? "处理中..." : "标记已完工" }}
                    </a>
                  </a-popconfirm>
                </template>
                <span v-else class="action-text action-muted">用户可评价</span>
                <a class="action-text action-detail" @click="openDetail(record)">查看详情</a>
              </div>
            </div>
          </template>
          </a-table>
        </div>
        <div class="table-pagination">
          <a-pagination
            :current="pagination.current"
            :pageSize="pagination.pageSize"
            :total="pagination.total"
            :showSizeChanger="true"
            :pageSizeOptions="pagination.pageSizeOptions"
            :showTotal="pagination.showTotal"
            @change="onPageChange"
            @showSizeChange="onPageSizeChange"
          />
        </div>
      </div>
    </div>

    <assign-caregiver-modal
      :visible="assignVisible"
      :order="assignOrder"
      :loading="assignLoading"
      @cancel="assignVisible = false"
      @confirm="handleAssign"
    />

    <dispatch-order-detail-modal
      :visible="detailVisible"
      :order="detailOrder"
      :loading="detailLoading"
      @cancel="detailVisible = false"
    />
  </div>
</template>

<script>
import AssignCaregiverModal from "./AssignCaregiverModal.vue";
import DispatchOrderDetailModal from "./DispatchOrderDetailModal.vue";
import { dispatchApi, ORDER_STATUS } from "./dispatchApi";
import { calcStickyTableLayout } from "./tableLayout";

export default {
  name: "DispatchOrderPool",
  components: { AssignCaregiverModal, DispatchOrderDetailModal },
  data() {
    return {
      loading: false,
      orders: [],
      stats: { pending: 0, serving: 0, onlineCaregivers: 0, doneToday: 0 },
      statusMap: ORDER_STATUS,
      tableScrollY: 360,
      tableHostStyle: {},
      tableBodyStyle: {},
      filters: {
        keyword: "",
        status: "all",
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["10", "30", "50", "100"],
        showTotal: (total) => `共 ${total} 条`,
      },
      assignVisible: false,
      assignOrder: null,
      assignLoading: false,
      detailVisible: false,
      detailOrder: null,
      detailLoading: false,
      completingId: null,
      statCards: [
        { key: "pending", label: "待指派订单", icon: "file-text", iconClass: "icon-red" },
        { key: "serving", label: "服务中订单", icon: "heart", iconClass: "icon-blue" },
        { key: "onlineCaregivers", label: "在线护工", icon: "team", iconClass: "icon-green" },
        { key: "doneToday", label: "今日完工", icon: "check-circle", iconClass: "icon-gray" },
      ],
      columns: [
        { title: "订单号", dataIndex: "orderNo", key: "orderNo", width: 140, scopedSlots: { customRender: "orderNo" } },
        { title: "提交时间", dataIndex: "submitTime", key: "submitTime", width: 150 },
        { title: "医院/科室/床位", key: "location", scopedSlots: { customRender: "location" } },
        { title: "患者信息", key: "patient", width: 220, scopedSlots: { customRender: "patient" } },
        { title: "服务时间", dataIndex: "dateRange", key: "dateRange", width: 130 },
        { title: "指派方式", key: "assign", width: 168, scopedSlots: { customRender: "assign" } },
        { title: "状态", dataIndex: "status", key: "status", width: 100, scopedSlots: { customRender: "status" } },
        { title: "操作", key: "action", width: 260, fixed: "right", scopedSlots: { customRender: "action" } },
      ],
    };
  },
  mounted() {
    this.loadStats();
    this.loadOrders();
    this.updateTableHeight();
    window.addEventListener("resize", this.updateTableHeight);
    this.$nextTick(() => {
      if (typeof ResizeObserver !== "undefined" && this.$refs.tableHost) {
        this.resizeObserver = new ResizeObserver(() => this.updateTableHeight());
        this.resizeObserver.observe(this.$refs.tableHost);
      }
      if (this.$refs.pageRoot) {
        this.resizeObserverPage = new ResizeObserver(() => this.updateTableHeight());
        this.resizeObserverPage.observe(this.$refs.pageRoot);
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateTableHeight);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.resizeObserverPage) {
      this.resizeObserverPage.disconnect();
    }
  },
  methods: {
    async loadStats() {
      try {
        this.stats = await dispatchApi.stats();
        this.$root.$emit("dispatch-stats-update", this.stats);
      } catch (e) {
        this.$message.error(e.message || "统计数据加载失败");
      }
    },
    async loadOrders() {
      this.loading = true;
      try {
        const data = await dispatchApi.pageOrders({
          keyword: this.filters.keyword.trim(),
          status: this.filters.status,
          current: this.pagination.current,
          size: this.pagination.pageSize,
        });
        this.orders = data.records || [];
        this.pagination.total = data.total || 0;
        this.pagination.current = data.current || this.pagination.current;
        this.pagination.pageSize = data.size || this.pagination.pageSize;
      } catch (e) {
        this.$message.error(e.message || "订单列表加载失败");
      } finally {
        this.loading = false;
        this.$nextTick(this.updateTableHeight);
      }
    },
    handleSearch() {
      this.pagination.current = 1;
      this.loadOrders();
    },
    resetFilters() {
      this.filters.keyword = "";
      this.filters.status = "all";
      this.pagination.current = 1;
      this.loadOrders();
    },
    onPageChange(page, pageSize) {
      this.pagination.current = page;
      this.pagination.pageSize = pageSize;
      this.loadOrders();
    },
    onPageSizeChange(current, size) {
      this.pagination.current = 1;
      this.pagination.pageSize = size;
      this.loadOrders();
    },
    updateTableHeight() {
      this.$nextTick(() => {
        const host = this.$refs.tableHost;
        if (!host) return;
        const { scrollY, hostHeight } = calcStickyTableLayout(host, {
          theadH: 55,
          paginationH: 56,
          bottomGap: 20,
          minScrollY: 260,
          minHostH: 320,
        });
        this.tableScrollY = scrollY;
        this.tableHostStyle = { height: `${hostHeight}px` };
        this.tableBodyStyle = { "--table-scroll-y": `${scrollY}px` };
      });
    },
    openAssign(order) {
      this.assignOrder = order;
      this.assignVisible = true;
    },
    assignTagColor(mode) {
      if (mode === "preferred") return "orange";
      if (mode === "random") return "blue";
      return "green";
    },
    statusTagColor(status) {
      const map = {
        pending: "red",
        pending_confirm: "orange",
        serving: "green",
        done: "blue",
      };
      return map[status] || "blue";
    },
    async handleAssign({ orderId, caregiverId }) {
      this.assignLoading = true;
      try {
        await dispatchApi.assignOrder(orderId, caregiverId);
        this.$message.success("指派成功");
        this.assignVisible = false;
        await this.loadStats();
        await this.loadOrders();
      } catch (e) {
        this.$message.error(e.message || "指派失败");
      } finally {
        this.assignLoading = false;
      }
    },
    async handleComplete(record) {
      if (!record || !record.id) return;
      this.completingId = record.id;
      try {
        await dispatchApi.completeOrder(record.id);
        this.$message.success("已标记完工，用户现在可以评价护工");
        await this.loadStats();
        await this.loadOrders();
      } catch (e) {
        this.$message.error(e.message || "操作失败");
      } finally {
        this.completingId = null;
      }
    },
    async openDetail(record) {
      if (!record || !record.id) return;
      this.detailVisible = true;
      this.detailOrder = record;
      this.detailLoading = true;
      try {
        this.detailOrder = await dispatchApi.orderDetail(record.id);
      } catch (e) {
        this.$message.error(e.message || "订单详情加载失败");
      } finally {
        this.detailLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.dispatch-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  box-sizing: border-box;
  overflow: hidden;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.stat-card {
  background: #fff;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.icon-red { background: #fef2f2; color: #ef4444; }
.icon-blue { background: #eff6ff; color: #3b82f6; }
.icon-green { background: #f0fdf4; color: #22c55e; }
.icon-gray { background: #f8fafc; color: #94a3b8; }

.stat-label {
  margin: 0;
  font-size: 0.8125rem;
  color: #64748b;
}

.stat-value {
  margin: 0.25rem 0 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1;
}

.pool-card {
  background: #fff;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.pool-card-fill {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.pool-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
  gap: 1rem;
}

.pool-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.pool-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}

.pool-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.query-input {
  width: 240px;
}

.query-select {
  width: 130px;
}

.table-host {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0 0.75rem 0.75rem;
  overflow: hidden;
}

.table-body-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.table-pagination {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: 0.75rem;
  margin-top: 0.25rem;
  border-top: 1px solid #f1f5f9;
}

.order-table {
  height: 100%;
}

.order-table >>> .ant-spin-nested-loading,
.order-table >>> .ant-spin-container,
.order-table >>> .ant-table,
.order-table >>> .ant-table-content,
.order-table >>> .ant-table-scroll {
  height: 100%;
}

.order-table >>> .ant-table-body {
  overflow-y: auto !important;
  min-height: var(--table-scroll-y, 360px) !important;
}

.order-table >>> .ant-table-placeholder {
  min-height: calc(var(--table-scroll-y, 360px) - 8px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.order-table >>> .ant-empty {
  margin: 0;
}

.order-no {
  font-family: ui-monospace, monospace;
  font-size: 0.8125rem;
  color: #334155;
}

.hospital-name {
  margin: 0;
  font-weight: 600;
  color: #0f172a;
  font-size: 0.875rem;
}

.dept-text {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  color: #64748b;
}

.cell-assign .assign-sub {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: #64748b;
}

.patient-name {
  font-weight: 600;
  color: #0f172a;
  margin-right: 0.375rem;
}

.patient-meta {
  font-size: 0.75rem;
  color: #64748b;
}

.assign-btn {
  border-radius: 0.5rem !important;
  font-weight: 600;
}

.action-cell {
  min-width: 15rem;
  padding: 0.125rem 0;
}

.action-links {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.875rem;
  white-space: nowrap;
}

.action-text {
  font-size: 0.8125rem;
  line-height: 1.4;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.15s;
}

.action-text:hover {
  opacity: 0.75;
}

.action-assign {
  color: #0082d4;
}

.action-complete {
  color: #16a34a;
}

.action-complete.is-loading {
  color: #94a3b8;
  cursor: wait;
}

.action-detail {
  color: #64748b;
}

.action-muted {
  color: #16a34a;
  cursor: default;
}

.action-muted:hover {
  opacity: 1;
}

.order-table >>> .ant-table-tbody > tr > td:last-child {
  padding-top: 0.625rem;
  padding-bottom: 0.625rem;
}

.detail-link {
  color: #0082d4;
  font-size: 0.8125rem;
  cursor: pointer;
}

@media (max-width: 1200px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .dispatch-page {
    padding: 1rem;
  }
  .stats-grid {
    grid-template-columns: 1fr;
  }
  .pool-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .query-input,
  .query-select {
    width: 100%;
  }
}
</style>
